import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { useSession } from "../app/session";
import { parseWs } from "../core/links";

/**
 * "Also in" (ADR 0024): the other workspaces this task or habit is kept in, as toggles. Only workspaces the person
 * may write to are offered; the server makes and updates the copies (and refuses where it may not write).
 */
export function AlsoIn({
  here,
  value,
  readOnly,
  onChange,
  testId = "also-in",
}: {
  here: string;
  /** `link.workspaces` / `linkWorkspaces` */
  value: unknown;
  readOnly?: boolean;
  onChange(others: string[]): void;
  testId?: string;
}) {
  const { t } = useTranslation();
  const workspaces = useSession((s) => s.session?.workspaces) ?? [];
  const writable = workspaces.filter((w) => w.id !== here && (w.role === "owner" || w.role === "member"));
  const on = new Set(parseWs(value).filter((w) => w !== here));
  if (writable.length === 0 && on.size === 0) return null;
  const names = new Map(workspaces.map((w) => [w.id, w.name]));
  const shown = [...writable.map((w) => w.id), ...[...on].filter((id) => !writable.some((w) => w.id === id))];
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-start gap-x-3" data-testid={testId}>
      <span className="pt-[5px] text-[12px] text-neutral-500">{t("linked.alsoIn")}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {shown.map((id) => {
          const pressed = on.has(id);
          return (
            <button
              key={id}
              type="button"
              aria-pressed={pressed}
              disabled={readOnly || !writable.some((w) => w.id === id)}
              data-testid="also-in-workspace"
              data-workspace={id}
              onClick={() => {
                const next = new Set(on);
                if (pressed) next.delete(id);
                else next.add(id);
                onChange([...next]);
              }}
              className={clsx(
                "rounded-full border px-2 py-0.5 text-[12px] disabled:opacity-60",
                pressed
                  ? "border-accent bg-accent-tint text-accent"
                  : "border-neutral-300 text-neutral-600 hover:border-neutral-700 dark:border-neutral-700 dark:text-neutral-300",
              )}
            >
              {names.get(id) ?? t("linked.otherWorkspace")}
            </button>
          );
        })}
      </div>
    </div>
  );
}
